import React from "react";
import { useParams } from "react-router-dom";
import { blogPosts } from "../../../constant/blogPosts";
import { COMPANY_DATA } from "../../../constant/CompanyData";
import { Helmet } from "react-helmet-async";

const BlogDetail = () => {
  const { id } = useParams();
  const blog = blogPosts.find((post) => post.id.toString() === id);

  if (!blog) {
    return (
      <>
        <Helmet>
          <title>Blog Post Not Found - QuantaRock</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <div className="p-10">Blog not found.</div>
      </>
    );
  }

  const metaDescription =
    blog.summary ||
    blog.contentHtml.slice(0, 150).replace(/<[^>]*>/g, "") + "...";
  const blogImage = require(`../../../assets/Blogs/${blog.image}`);
  const blogUrl = `${COMPANY_DATA.webDomain}resources/blogs/${id}`;
  const imageUrl = `${COMPANY_DATA.webDomain.replace(/\/$/, "")}${blogImage}`;

  const jsonLdArticle = () => {
    const parsedDate = Date.parse(blog.date);
    const datePublished = isNaN(parsedDate)
      ? blog.date
      : new Date(parsedDate).toISOString();
    // derive some additional metadata from the content
    const contentText = blog.contentHtml
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    const wordCount = contentText
      ? contentText.split(/\s+/).filter(Boolean).length
      : 0;
    const articleSection = contentText.slice(0, 160);
    const keywords = blog.title
      ? blog.title
          .replace(/[\W_]+/g, " ")
          .split(/\s+/)
          .map((w) => w.toLowerCase())
          .slice(0, 6)
      : [];

    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": blogUrl,
      },
      headline: blog.title,
      description: metaDescription,
      image: [imageUrl],
      datePublished: datePublished,
      dateModified: datePublished,
      wordCount: wordCount,
      articleSection: articleSection,
      keywords: keywords,
      author: {
        "@type": "Person",
        name: blog.author,
      },
      publisher: {
        "@type": "Organization",
        name: "QuantaRock",
        logo: {
          "@type": "ImageObject",
          url: "https://quantarock.in/assets/LogoMain.png",
        },
      },
      isAccessibleForFree: true,
    };
    return JSON.stringify(ld);
  };

  return (
    <>
      <Helmet>
        <title>{`${blog.title} | QuantaRock Blog`}</title>
        <meta name="description" content={metaDescription} />
        <meta
          name="keywords"
          content="colocation, data center, hosting, server, cloud, IT infrastructure"
        />
        <link rel="canonical" href={blogUrl} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={blogUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="article:published_time" content={blog.date} />
        <meta name="article:author" content={blog.author} />
      </Helmet>
      <script type="application/ld+json">{jsonLdArticle()}</script>
      <div className="min-h-screen bg-white py-20 px-6 md:px-24">
        <div className="max-w-4xl mx-auto">
          <img
            src={blogImage}
            alt={blog.title}
            className="w-full md:w-1/3 h-auto max-h-72 object-contain rounded-lg mb-6 md:mr-6 md:float-left"
            loading="lazy"
            width={900}
            height={600}
          />
          <p className="text-sm text-gray-500">
            {blog.date} • {blog.author}
          </p>
          <h1 className="text-4xl font-bold text-gray-800 mt-2 mb-6">
            {blog.title}
          </h1>
          <div
            className="text-gray-700 leading-relaxed space-y-5 text-lg [&_ul]:list-disc [&_ul]:ml-6 [&_hr]:my-6 [&_img]:w-full [&_img]:h-auto [&_img]:object-contain"
            dangerouslySetInnerHTML={{ __html: blog.contentHtml }}
          />
        </div>
      </div>
    </>
  );
};

export default BlogDetail;
